import React, { useEffect, useMemo, useState } from 'react';
import { Info, Copy } from 'lucide-react';
import DashboardLayout from '../../../components/layouts/DashboardLayout';
import InputComp from '../../../components/InputComp';
import TextareaComp from '../../../components/TextareaComp';
import ButtonComp from '../../../components/ButtonComp';
import AccountHeader from '../../../components/Account/AccountHeader';
import InfoSection from '../../../components/Dashboard/InfoSection';
import { formatDate } from '../../../utils/date';
import SpinnerComp from '../../../components/SpinnerComp';
import useAccount from '../../../hooks/useAccount';
import { Locate, X } from 'lucide-react';
import PlacesSearchComp from '../../../components/Google/PlacesSearchComp';
import FormField from '../../../components/Dashboard/Account/FormField';
import useLocation from '../../../hooks/useLocation';
import { useNavigate } from 'react-router-dom';
import { useModalStore } from '../../../store/useModalStore'
import HomeModals from '../Modals/HomeModals'
import { parseAddress } from '../../../utils/parseAddress';
import UserBadge from '../../../components/Dashboard/Account/UserBadge';
import ProfileUserIcon from '../../../assets/profile-user.svg';
import { getBadgeImage, getBadgeInfo } from '../../../utils/badges';
import { Address } from '../../../types/address';
import { toast } from 'react-toastify';

const Account = () => {
  const {
    user,
    fileInputRef,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    handle,
    setHandle,
    description,
    setDescription,
    isMemberSinceVisible,
    setIsMemberSinceVisible,
    isCigarsSmokedVisible,
    setIsCigarsSmokedVisible,
    isLocationVisible,
    setIsLocationVisible,
    loading,
    handleProfilePictureChange,
    handleProfilePictureDelete,
    handleSaveChanges,
    handleUploadButtonClick
  } = useAccount();

  const {
    showLocation,
    setShowLocation,
    useCurrentLocation,
    handlelocationClick,
    location,
    locationLoading,
    showPlacesSearch,
    handlePlaceSelect,
    locationError,
    setShowTryAgain,
    setShowPlacesSearch,
    latitude,
    longitude,
    homeLocation
  } = useLocation();
  const { modalStates, updateModalState } = useModalStore();

  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);

  // Add saved state references to compare against
  const [savedState, setSavedState] = useState<{
    firstName: string;
    lastName: string;
    description: string;
    handle: string;
    memberSinceVisible: boolean;
    cigarsVisible: boolean;
    locationVisible: boolean;
    location: Address | null;
  }>({
    firstName: '',
    lastName: '',
    description: '',
    handle: '',
    memberSinceVisible: false,
    cigarsVisible: false,
    locationVisible: false,
    location: null
  });

  useEffect(() => {
    if (locationError) {
      setShowTryAgain(true);
      setShowPlacesSearch(true);
    }
  }, [locationError]);

  useEffect(() => {
    setIsLocationVisible(showLocation);
  }, [useCurrentLocation]);

  useEffect(() => {
    if (user.privacies.location_visible) {
      setShowLocation(true);
    }
  }, []);


  useEffect(() => {
    setFirstName(user.first_name || '');
    setLastName(user.last_name || '');
    setDescription(user.bio || '');
    setHandle(user.handle || '');
    setIsMemberSinceVisible(user.privacies.member_since_visible);
    setIsCigarsSmokedVisible(user.privacies.cigars_smoked_visible);
    setIsLocationVisible(user.privacies.location_visible);
    setIsInitialized(true);

    // Also update saved state when user changes
    setSavedState({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      description: user.bio || '',
      handle: user.handle || '',
      memberSinceVisible: user.privacies.member_since_visible,
      cigarsVisible: user.privacies.cigars_smoked_visible,
      locationVisible: user.privacies.location_visible,
      location: user.location
    });
  }, [user]);

  const handleSave = async () => {
    await handleSaveChanges(location);
    // Update saved state after successful save
    setSavedState({
      firstName,
      lastName,
      description,
      handle,
      memberSinceVisible: isMemberSinceVisible,
      cigarsVisible: isCigarsSmokedVisible,
      locationVisible: isLocationVisible,
      location
    });
  }

  const hasChanges = useMemo(() => {
    if (!isInitialized) {
      return false;
    }
    const currentValues = {
      firstName,
      lastName,
      description,
      handle,
      memberSinceVisible: isMemberSinceVisible,
      cigarsVisible: isCigarsSmokedVisible,
      locationVisible: isLocationVisible,
      location
    };

    return JSON.stringify(savedState) !== JSON.stringify(currentValues);
  }, [
    firstName,
    lastName,
    description,
    handle,
    isMemberSinceVisible,
    isCigarsSmokedVisible,
    isLocationVisible,
    location,
    savedState,
    isInitialized
  ]);

  const copyReferralLink = () => {
    if (user.referral_link) {
      navigator.clipboard.writeText(user.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  return (
    <DashboardLayout title='Account' description=''>
      <div className="mb-12 md:mb-12 text-white rounded-lg pb-16 md:pb-24">
        {/* Add cigar */}
        <AccountHeader
          title="My Account"
          onAddPress={() => updateModalState('isModalOpen', true)}
          menuSelected='account'
        />
        <HomeModals
          {...modalStates}
          setIsModalOpen={(value) => updateModalState('isModalOpen', value)}
          setIsCigarModalOpen={(value) => updateModalState('isCigarModalOpen', value)}
        />
        <div className="block md:grid md:grid-cols-3 gap-6">
          {user.referral_link && (
            <FormField label="Referral Link" description="Share your referral link with friends">
              <InputComp
                placeholder="https://app.stoiccigars.com/referral/your_handle"
                value={user.referral_link}
                disabled={true}
                end_button={<button
                  onClick={copyReferralLink}
                  className="pl-2 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  <Copy size={24} />
                </button>}
              />
            </FormField>
          )}
          <FormField label="Your Avatar" description="Update your profile picture">
            <div className="flex items-center space-x-4">
              {loading ? (
                <SpinnerComp />
              ) : (
                <>
                  <img
                    src={user.profile_picture || ProfileUserIcon}
                    alt="Profile"
                    className="w-24 h-24 rounded-full"
                  />
                  <div className="flex space-x-4">
                    <input
                      className="hidden"
                      type="file"
                      ref={fileInputRef}
                      onChange={handleProfilePictureChange}
                    />
                    <button
                      className="text-teal-400 hover:text-teal-300 transition-colors"
                      onClick={handleUploadButtonClick}
                    >
                      Upload {user.profile_picture && 'New'}
                    </button>
                    {user.profile_picture && (
                      <button
                        className="text-red-500 hover:text-red-400 transition-colors"
                        onClick={handleProfilePictureDelete}
                        disabled={!user.profile_picture || loading}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}

            </div>

          </FormField>

          <FormField label="Full Name" description="Update your full name">
            <div className="grid grid-cols-2 gap-4">
              <InputComp
                placeholder="Phoenix"
                value={firstName}
                onChange={setFirstName}
              />
              <InputComp
                placeholder="Baker"
                value={lastName}
                onChange={setLastName}
              />
            </div>
          </FormField>

          <FormField label="Email Address" description="Update your email address">
            <InputComp
              placeholder="email@example.com"
              disabled={true}
              value={email}
              onChange={setEmail}
            />
          </FormField>

          <FormField label="Member ID" description="Your unique identifier (5-12 characters, only letters, numbers, _, .)">
            <InputComp
              placeholder="Member ID"
              value={handle}
              disabled={false}
              onChange={(value: string) => {
                // Only allow alphanumeric characters, underscores, and periods
                if (!value || /^[a-zA-Z0-9_.]{0,12}$/.test(value)) {
                  setHandle(value);
                }
              }}
            />
            {handle && handle.length < 5 && (
              <p className="text-red-500 text-sm mt-1">Handle must be at least 5 characters long.</p>
            )}
            {handle && !/^[a-zA-Z0-9_.]+$/.test(handle) && (
              <p className="text-red-500 text-sm mt-1">Handle can only contain letters, numbers, underscores (_), and periods (.).</p>
            )}
          </FormField>

          <FormField label="About you" description="Update your bio">
            <TextareaComp
              placeholder="Enter description"
              value={description}
              rows={3}
              onChange={setDescription}
            />
          </FormField>

          <FormField label="Member Since" description="Update your member since date">
            <InfoSection
              icon={<Info size={16} />}
              text={formatDate(user.created_at)}
              isOn={isMemberSinceVisible}
              onToggle={() => setIsMemberSinceVisible(!isMemberSinceVisible)}
            />
          </FormField>

          <FormField label="Cigars Smoked" description="Update your cigars smoked">
            <InfoSection
              icon={<Info size={16} />}
              text={user.cigars}
              isOn={isCigarsSmokedVisible}
              onToggle={() => setIsCigarsSmokedVisible(!isCigarsSmokedVisible)}
            />
          </FormField>

          <FormField label="Show location" description="Update your location visibility">
            <InfoSection
              isOn={showLocation}
              onToggle={() => setShowLocation(!showLocation)}
              icon={<Info size={16} />}
              text="Show Location"
            />
          </FormField>

          {showLocation && (
            <FormField label="Current location" description="Update your current location">
              <InfoSection
                isOn={useCurrentLocation}
                onToggle={handlelocationClick}
                icon={useCurrentLocation ? <Locate /> : <X />}
                text=
                {location
                  ? parseAddress(location)
                  : (locationLoading ? 'Loading...' : "Current Location")}
              />
            </FormField>)}
          {((showPlacesSearch && location == null) || (!homeLocation && location == null) && !locationLoading) && (
            <>
              <FormField label="Place search" description="Search for a place">

                <p className='mt-2 text-red-500 text-sm'>
                  Failed to get current location. Please search for a place.
                </p>
                <PlacesSearchComp onPlaceSelect={handlePlaceSelect} />
              </FormField>
            </>
          )}
          <FormField label="User Badges" description="The Badges you have earned">
            <div className="flex items-center space-y-4 gap-4">
              <UserBadge />
            </div>
          </FormField>
        </div>
        {hasChanges && (
          <div className='w-full flex justify-start mt-8'>
            <div className='w-full md:w-auto md:mr-12'>
              <ButtonComp
                fullWidthOnMobile={true}
                onClick={handleSave}
                loading={loading}
              >
                <span className='text-center'>Save Changes</span>
              </ButtonComp>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Account;